"use client"
import React, { useState, useRef, useEffect, useMemo } from 'react';
import styles from './CKEditor.module.css';
import modalStyles from './GroupDetailModal.module.css';
import { sanitizeHtml } from '@/utils/sanitize-html';
import {
  X,
  Globe,
  Volume2,
  Settings,
  Calendar,
  User,
  Hash,
  Mic,
  Save,
  Edit3,
  Eye,
  EyeOff,
  Folder,
  Bold,
  Italic,
  Underline,
  List,
  Link,
  Type,
  AlignLeft,
  AlignCenter,
  AlignRight
} from 'lucide-react';
import CKEditorWrapper from '@/components/CKEditor/CKEditorWrapper';
import groupSceneServices from "../../utils/services/groupScene";
import languageServices from "../../utils/services/language";
import s3UploadService from "../../utils/services/s3upload";
import { useIdPath } from "@/contexts/IdPathProvider";
import useAutoTranslate, { TranslationBudgetEstimate } from '@/hooks/useAutoTranslate';
import { globalUtils } from '@/utils/tools/globalUtils';
import TranslateButton from '@/components/shared/TranslateButton';
import VoiceSelectionPopup from '@/components/shared/VoiceSelectionPopup';
import TTSGeneratorSection from '@/components/shared/TTSGeneratorSection';
import SubtitleCueEditor from '@/components/shared/SubtitleCueEditor/SubtitleCueEditor';
import useVoiceTTSResources from '@/components/shared/useVoiceTTSResources';
import QrCodeBadge from '../exhibitQrCode/components/QrCodeBadge';

const availableVoiceCodes: Record<string, { nam: string; nu: string }> = {
  vi: { nam: 'vi-VN-Standard-B', nu: 'vi-VN-Standard-A' },
  en: { nam: 'en-US-Standard-B', nu: 'en-US-Standard-A' },
  zh: { nam: 'cmn-CN-Standard-B', nu: 'cmn-CN-Standard-A' },
  es: { nam: 'es-ES-Standard-B', nu: 'es-ES-Standard-A' },
  de: { nam: 'de-DE-Standard-B', nu: 'de-DE-Standard-A' }
};

const getVoiceFromPopup = (langCode: string, gender: 'nam' | 'nu') => {
  const normalizedCode = langCode?.toLowerCase();
  let langVoices = availableVoiceCodes[normalizedCode];

  if (!langVoices) {
    if (normalizedCode === 'vn' || normalizedCode === 'vi-vn' || normalizedCode === 'vi') {
      langVoices = availableVoiceCodes['vi'];
    } else if (normalizedCode === 'en' || normalizedCode === 'en-us') {
      langVoices = availableVoiceCodes['en'];
    } else if (normalizedCode === 'zh' || normalizedCode === 'cmn-cn' || normalizedCode === 'zh-cn') {
      langVoices = availableVoiceCodes['zh'];
    } else if (normalizedCode === 'es' || normalizedCode === 'es-es') {
      langVoices = availableVoiceCodes['es'];
    } else if (normalizedCode === 'de' || normalizedCode === 'de-de') {
      langVoices = availableVoiceCodes['de'];
    } else {
      langVoices = availableVoiceCodes['vi'];
    }
  }

  return langVoices?.[gender] || availableVoiceCodes['vi'][gender];
};

type GroupVoice = {
  language_id: string;
  gender: string;
  audio_path?: string | null;
};

type GroupTranslation = {
  language_id: string;
  language_name?: string;
  language_full_name?: string;
  title?: string | null;
  information?: string | null;
  transcript?: string | null;
  flag?: string | null;
};

const toApiGender = (gender: 'male' | 'female') => gender === 'male' ? 'nam' : 'nu';

const normalizeLangKey = (val?: any): string => {
  if (val === null || val === undefined) return '';
  const str = String(val).trim().toLowerCase();
  if (str === 'vn' || str === 'vie') return 'vi';
  if (str === 'us' || str === 'gb' || str === 'eng') return 'en';
  if (str === 'jp' || str === 'jpn') return 'ja';
  if (str === 'kr' || str === 'kor') return 'ko';
  if (str === 'cn' || str === 'zho' || str === 'chi') return 'zh';
  return str;
};

const isSameLanguage = (trans: any, projectLang: any): boolean => {
  if (!trans || !projectLang) return false;

  const tId = trans.language_id ?? trans.id;
  const pId = projectLang.id ?? projectLang.language_id;

  if (tId !== undefined && pId !== undefined) {
    if (String(tId).trim() === String(pId).trim()) return true;
    if (normalizeLangKey(tId) === normalizeLangKey(pId)) return true;
  }

  const tCode = trans.language_code || trans.code || trans.lang_code;
  const pCode = projectLang.code || projectLang.language_code;
  if (tCode && pCode) {
    if (String(tCode).trim().toLowerCase() === String(pCode).trim().toLowerCase()) return true;
    if (normalizeLangKey(tCode) === normalizeLangKey(pCode)) return true;
  }

  if (tId && pCode && normalizeLangKey(tId) === normalizeLangKey(pCode)) return true;
  if (tCode && pId && normalizeLangKey(tCode) === normalizeLangKey(pId)) return true;

  const tName = trans.language_name || trans.name || trans.language_full_name || trans.full_name;
  const pName = projectLang.name || projectLang.full_name;
  if (tName && pName && String(tName).trim().toLowerCase() === String(pName).trim().toLowerCase()) {
    return true;
  }

  return false;
};

const getGroupVoice = (voices: GroupVoice[] | undefined, languageId: string, gender: 'male' | 'female') => {
  const apiGender = toApiGender(gender);
  return voices?.find((voice) =>
    isSameLanguage({ language_id: voice.language_id }, { id: languageId }) && voice.gender === apiGender
  );
};

const MemoizedHtmlRenderer: React.FC<{ html?: string | null }> = React.memo(({ html }) => {
  if (!html) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 text-2xl mb-2">📝</div>
        <span className="text-gray-400 italic">Chưa có thông tin chi tiết</span>
        <div className="text-xs text-gray-500 mt-1">Nhấn "Chỉnh sửa" để thêm nội dung</div>
      </div>
    );
  }

  return <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />;
});
MemoizedHtmlRenderer.displayName = 'MemoizedHtmlRenderer';
interface GroupDetailModalProps {
  group: any;
  isOpen: boolean;
  onClose: () => void;
  editingTranslation: string | null;
  setEditingTranslation: (id: string | null) => void;
  loading: boolean;
  onRefresh?: () => void; // Add callback to refresh group data
}

// Enhanced Rich Text Editor Component
interface EnhancedRichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: string;
}

const EnhancedRichTextEditor: React.FC<EnhancedRichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Nhập nội dung...",
  height = "300px"
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [internalValue, setInternalValue] = useState(value);
  const { idPath } = useIdPath();

  // Update internal value when external value changes
  React.useEffect(() => {
    if (value !== internalValue) {
      setInternalValue(value);
      if (editorRef.current) {
        // Only update DOM if editor is not currently focused
        if (document.activeElement !== editorRef.current) {
          editorRef.current.innerHTML = value;
        }
      }
    }
  }, [value]);

  // Initialize editor with value when it first mounts
  React.useEffect(() => {
    if (editorRef.current && value) {
      editorRef.current.innerHTML = value;
      setInternalValue(value);
    }
  }, []);

  // Additional effect to ensure content is set when component becomes visible
  React.useEffect(() => {
    if (editorRef.current && value && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
      setInternalValue(value);
    }
  }, [value]);

  const saveCursorPosition = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      return selection.getRangeAt(0);
    }
    return null;
  };

  const restoreCursorPosition = (range: Range | null) => {
    if (range) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        try {
          selection.addRange(range);
        } catch (error) {
          // If range is invalid, place cursor at end
          const newRange = document.createRange();
          newRange.selectNodeContents(editorRef.current!);
          newRange.collapse(false);
          selection.addRange(newRange);
        }
      }
    }
  };

  const handleCommand = (command: string, value?: string) => {
    try {
      const savedRange = saveCursorPosition();
      document.execCommand(command, false, value);
      if (editorRef.current) {
        const newContent = editorRef.current.innerHTML;
        setInternalValue(newContent);
        onChange(newContent);
      }
      // Restore cursor position after a small delay
      setTimeout(() => restoreCursorPosition(savedRange), 10);
    } catch (error) {
      console.error('Command error:', error);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      const newContent = editorRef.current.innerHTML;
      setInternalValue(newContent);
      onChange(newContent);
    }
  };

  const insertLink = () => {
    const url = prompt('Nhập URL:');
    if (url) {
      handleCommand('createLink', url);
    }
  };

  const formatText = (command: string) => {
    handleCommand(command);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const insertTable = () => {
    const rows = prompt('Số hàng:', '3');
    const cols = prompt('Số cột:', '3');

    if (rows && cols) {
      const savedRange = saveCursorPosition();
      let tableHTML = '<table border="1" style="border-collapse: collapse; width: 100%; margin: 10px 0;">';
      for (let i = 0; i < parseInt(rows); i++) {
        tableHTML += '<tr>';
        for (let j = 0; j < parseInt(cols); j++) {
          tableHTML += '<td style="padding: 8px; border: 1px solid #ccc;">Ô ' + (i + 1) + '-' + (j + 1) + '</td>';
        }
        tableHTML += '</tr>';
      }
      tableHTML += '</table>';

      document.execCommand('insertHTML', false, tableHTML);
      if (editorRef.current) {
        const newContent = editorRef.current.innerHTML;
        setInternalValue(newContent);
        onChange(newContent);
      }
      setTimeout(() => restoreCursorPosition(savedRange), 10);
    }
  };

  const insertImage = () => {
    const url = prompt('Nhập URL hình ảnh:');
    if (url) {
      const savedRange = saveCursorPosition();
      const imageHTML = `<img src="${url}" alt="Hình ảnh" style="max-width: 100%; height: auto; margin: 10px 0;" />`;
      document.execCommand('insertHTML', false, imageHTML);
      if (editorRef.current) {
        const newContent = editorRef.current.innerHTML;
        setInternalValue(newContent);
        onChange(newContent);
      }
      setTimeout(() => restoreCursorPosition(savedRange), 10);
    }
  };

  const insertBlockquote = () => {
    const selection = window.getSelection();
    const savedRange = saveCursorPosition();

    if (selection && selection.toString()) {
      const selectedText = selection.toString();
      const blockquoteHTML = `<blockquote style="border-left: 4px solid #ccc; margin: 16px 0; padding-left: 16px; font-style: italic; color: #666;">${selectedText}</blockquote>`;
      document.execCommand('insertHTML', false, blockquoteHTML);
    } else {
      const blockquoteHTML = `<blockquote style="border-left: 4px solid #ccc; margin: 16px 0; padding-left: 16px; font-style: italic; color: #666;">Trích dẫn...</blockquote>`;
      document.execCommand('insertHTML', false, blockquoteHTML);
    }

    if (editorRef.current) {
      const newContent = editorRef.current.innerHTML;
      setInternalValue(newContent);
      onChange(newContent);
    }
    setTimeout(() => restoreCursorPosition(savedRange), 10);
  };

  const insertCodeBlock = () => {
    const code = prompt('Nhập code:', 'console.log("Hello World");');
    if (code) {
      const savedRange = saveCursorPosition();
      const codeHTML = `<pre style="background: #f4f4f4; border: 1px solid #ddd; border-radius: 4px; padding: 12px; overflow-x: auto; font-family: monospace; margin: 10px 0;"><code>${code}</code></pre>`;
      document.execCommand('insertHTML', false, codeHTML);
      if (editorRef.current) {
        const newContent = editorRef.current.innerHTML;
        setInternalValue(newContent);
        onChange(newContent);
      }
      setTimeout(() => restoreCursorPosition(savedRange), 10);
    }
  };



  return (
    <div className={`${styles.ckEditorContainer} ${isLoading ? styles.loading : ''}`}>
      {/* Enhanced Toolbar */}
      <div className="bg-gray-50 border-b border-gray-200 p-3 flex flex-wrap items-center gap-2">
        {/* Text Formatting */}
        <div className="flex items-center gap-1 border-r border-gray-300 pr-3 mr-3">
          <button
            type="button"
            onClick={() => formatText('bold')}
            className="p-2 hover:bg-gray-200 rounded text-gray-600 transition-colors"
            title="In đậm (Ctrl+B)"
          >
            <Bold className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => formatText('italic')}
            className="p-2 hover:bg-gray-200 rounded text-gray-600 transition-colors"
            title="In nghiêng (Ctrl+I)"
          >
            <Italic className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => formatText('underline')}
            className="p-2 hover:bg-gray-200 rounded text-gray-600 transition-colors"
            title="Gạch chân (Ctrl+U)"
          >
            <Underline className="h-4 w-4" />
          </button>
        </div>

        {/* Headings */}
        <div className="flex items-center gap-1 border-r border-gray-300 pr-3 mr-3">
          <select
            onChange={(e) => {
              if (e.target.value) {
                handleCommand('formatBlock', e.target.value);
              }
            }}
            className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            defaultValue=""
          >
            <option value="">Chọn định dạng</option>
            <option value="h1">Tiêu đề 1</option>
            <option value="h2">Tiêu đề 2</option>
            <option value="h3">Tiêu đề 3</option>
            <option value="h4">Tiêu đề 4</option>
            <option value="p">Đoạn văn</option>
          </select>
        </div>

        {/* Lists */}
        <div className="flex items-center gap-1 border-r border-gray-300 pr-3 mr-3">
          <button
            type="button"
            onClick={() => formatText('insertUnorderedList')}
            className="p-2 hover:bg-gray-200 rounded text-gray-600 transition-colors"
            title="Danh sách có dấu đầu dòng"
          >
            <List className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => formatText('insertOrderedList')}
            className="p-2 hover:bg-gray-200 rounded text-gray-600 transition-colors"
            title="Danh sách có số thứ tự"
          >
            <Type className="h-4 w-4" />
          </button>
        </div>

        {/* Alignment */}
        <div className="flex items-center gap-1 border-r border-gray-300 pr-3 mr-3">
          <button
            type="button"
            onClick={() => formatText('justifyLeft')}
            className="p-2 hover:bg-gray-200 rounded text-gray-600 transition-colors"
            title="Căn trái"
          >
            <AlignLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => formatText('justifyCenter')}
            className="p-2 hover:bg-gray-200 rounded text-gray-600 transition-colors"
            title="Căn giữa"
          >
            <AlignCenter className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => formatText('justifyRight')}
            className="p-2 hover:bg-gray-200 rounded text-gray-600 transition-colors"
            title="Căn phải"
          >
            <AlignRight className="h-4 w-4" />
          </button>
        </div>

        {/* Advanced Tools */}
        <div className="flex items-center gap-1 border-r border-gray-300 pr-3 mr-3">
          <button
            type="button"
            onClick={insertLink}
            className="p-2 hover:bg-gray-200 rounded text-gray-600 transition-colors"
            title="Chèn liên kết"
          >
            <Link className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={insertImage}
            className="px-2 py-1 text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
            title="Chèn hình ảnh"
          >
            📷 Ảnh
          </button>
          <button
            type="button"
            onClick={insertTable}
            className="px-2 py-1 text-xs bg-green-50 text-green-600 border border-green-200 rounded hover:bg-green-100 transition-colors"
            title="Chèn bảng"
          >
            📊 Bảng
          </button>
        </div>

        {/* Content Tools */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={insertBlockquote}
            className="px-2 py-1 text-xs bg-purple-50 text-purple-600 border border-purple-200 rounded hover:bg-purple-100 transition-colors"
            title="Trích dẫn"
          >
            💬 Trích dẫn
          </button>
          <button
            type="button"
            onClick={insertCodeBlock}
            className="px-2 py-1 text-xs bg-gray-50 text-gray-600 border border-gray-200 rounded hover:bg-gray-100 transition-colors"
            title="Code block"
          >
            💻 Code
          </button>
          <button
            type="button"
            onClick={() => formatText('removeFormat')}
            className="px-2 py-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100 transition-colors"
            title="Xóa định dạng"
          >
            🧹 Xóa
          </button>
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className={`p-4 outline-none overflow-y-auto focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${modalStyles.enhancedRichEditor}`}
        style={{
          minHeight: height,
          border: 'none'
        }}
        onInput={handleInput}
        onPaste={handlePaste}
        onFocus={() => {
          // Ensure content is set when editor gets focus
          if (editorRef.current && value && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value;
          }
        }}
        data-placeholder={!internalValue && !value ? placeholder : ''}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) || '' }}
      />
    </div>
  );
};

// ─── Parallax Settings Panel ──────────────────────────────────────────────────
const ParallaxSettingsPanel: React.FC<{
  groupId: string;
  initialEnabled: boolean;
  initialSensitivity: number;
  idPath: string;
  onRefresh?: () => void;
}> = ({ groupId, initialEnabled, initialSensitivity, idPath, onRefresh }) => {
  const [enabled, setEnabled] = useState(initialEnabled);
  const [sensitivity, setSensitivity] = useState(initialSensitivity ?? 10);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Sync state whenever the parent passes updated values (e.g. modal re-opens with fresh data)
  useEffect(() => {
    setEnabled(initialEnabled);
  }, [initialEnabled]);

  useEffect(() => {
    setSensitivity(initialSensitivity ?? 10);
  }, [initialSensitivity]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await groupSceneServices.updateGroup({
        id_path: idPath,
        group_id: groupId,
        body: {
          id: groupId,
          mouse_parallax: enabled ? 1 : 0,
          mouse_parallax_sensitivity: sensitivity,
          version_id: 1,
        }
      });
      if (response?.success !== false) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        if (onRefresh) onRefresh();
      }
    } catch (_e) {
      // silently fail
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Toggle */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Bật hiệu ứng parallax cho nhóm này</span>
        <button
          type="button"
          onClick={() => setEnabled(v => !v)}
          className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none ${
            enabled ? 'bg-blue-600' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block w-4 h-4 transform bg-white rounded-full shadow transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Sensitivity slider */}
      {enabled && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm text-gray-600">Độ nhạy: <span className="font-semibold text-blue-700">{sensitivity.toFixed(1)}°</span></label>
            <span className="text-xs text-gray-400">1° – 30°</span>
          </div>
          <input
            type="range"
            min={1}
            max={30}
            step={0.5}
            value={sensitivity}
            onChange={(e) => setSensitivity(parseFloat(e.target.value))}
            className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>Nhẹ nhàng</span>
            <span>Mạnh mẽ</span>
          </div>
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={isSaving}
        className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
          saved
            ? 'bg-green-500 text-white'
            : 'bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300'
        }`}
      >
        {isSaving ? (
          <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" /><span>Đang lưu...</span></>
        ) : saved ? (
          <><span>✓</span><span>Đã lưu!</span></>
        ) : (
          <span>Lưu cài đặt Parallax</span>
        )}
      </button>
    </div>
  );
};

const GroupDetailModal: React.FC<GroupDetailModalProps> = ({
  group,
  isOpen,
  onClose,
  editingTranslation,
  setEditingTranslation,
  loading,
  onRefresh
}) => {
  const [activeTab, setActiveTab] = useState<'translations' | 'audio' | 'settings'>('translations');
  const [editedTranslations, setEditedTranslations] = useState<{ [key: string]: { title: string, information: string } }>({});
  const [localTranslations, setLocalTranslations] = useState<GroupTranslation[]>(() => {
    const raw = (group?.translations || []) as GroupTranslation[];
    const seen = new Set<string>();
    const unique: GroupTranslation[] = [];
    raw.forEach(t => {
      const k = normalizeLangKey(t.language_id);
      if (!seen.has(k)) {
        seen.add(k);
        unique.push(t);
      }
    });
    return unique.sort((a, b) => {
      const aDef = a.language_id === 'vn' || a.language_id === 'vi';
      const bDef = b.language_id === 'vn' || b.language_id === 'vi';
      if (aDef && !bDef) return -1;
      if (!aDef && bDef) return 1;
      return 0;
    });
  });
  const [isLoading, setIsLoading] = useState(false);
  const [voiceUrls, setVoiceUrls] = useState<{ [key: string]: { male?: string, female?: string } }>({});
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [voiceContents, setVoiceContents] = useState<{ [key: string]: string }>({});
  const [activeAudioLangId, setActiveAudioLangId] = useState<string>('');
  const [activeSubtitleVariants, setActiveSubtitleVariants] = useState<Record<string, 'nam' | 'nu'>>({});
  const [showVoiceListPopup, setShowVoiceListPopup] = useState(false); // Track voice list popup visibility
  const [showVoicePopup, setShowVoicePopup] = useState(null); // Track individual voice popup: {langId, gender}
  const [selectedVoices, setSelectedVoices] = useState<{ [key: string]: { nam: string, nu: string } }>({});
  const [projectLanguages, setProjectLanguages] = useState<any[]>([]);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false);
  const [isUploadingEditorImage, setIsUploadingEditorImage] = useState(false);
  const [translationBudgetEstimate, setTranslationBudgetEstimate] = useState<TranslationBudgetEstimate | null>(null);
  const translationBudgetResolverRef = useRef<((confirmed: boolean) => void) | null>(null);
  const { idPath, namePath } = useIdPath();
  const {
    ttsConfig,
    setTtsConfig,
    ttsResources,
    refreshProjectPresets,
    buildTTSRequest
  } = useVoiceTTSResources(idPath, { enabled: isOpen });

  const showSuccess = (message: string) => {
    try {
      globalUtils.general.showToast(message || 'Thành công', 'Success', 'success', true, 3000);
    } catch (e) {
      console.log(message);
    }
  };
  const showError = (message: string) => {
    try {
      globalUtils.general.showToast(message || 'Lỗi', 'Error', 'error', true, 5000);
    } catch (e) {
      console.error(message);
    }
  };
  const showWarning = (message: string) => {
    try {
      globalUtils.general.showToast(message || 'Cảnh báo', 'Warning', 'warning', true, 4000);
    } catch (e) {
      console.warn(message);
    }
  };

  const handleEditorImageUpload = async (file: File): Promise<string> => {
    setIsUploadingEditorImage(true);
    try {
      const cleanPath = namePath ? namePath.replace(/admin\/?/, "") : "";
      const formattedPath = cleanPath ? (cleanPath.endsWith('/') ? cleanPath : `${cleanPath}/`) : '';
      const subfolder = `${formattedPath}upload/image/editor_content`;

      const response = await s3UploadService.uploadToS3(idPath, {
        body: { file },
        subfolder
      });

      if (response?.data?.success && response.data.file_url) {
        return response.data.file_url;
      }

      const errorMsg = response?.data?.message || 'Có lỗi khi upload ảnh.';
      console.error('❌ CKEditor upload failed. Response:', response);
      throw new Error(errorMsg);
    } catch (error: any) {
      console.error('💥 Error uploading CKEditor image:', error);
      throw error;
    } finally {
      setIsUploadingEditorImage(false);
    }
  };

  // Debug log for selectedVoices changes
  useEffect(() => {
  }, [selectedVoices]);

  // Fetch available project languages when modal opens
  useEffect(() => {
    if (!isOpen || !idPath) return;

    let isMounted = true;
    const fetchProjectLanguages = async () => {
      try {
        setIsLoadingLanguages(true);
        const response = await languageServices.getLanguageList(idPath, true);
        const languageData = response?.data?.data || response?.data || response;
        if (Array.isArray(languageData) && isMounted) {
          const uniqueLanguagesMap = new Map<string, any>();
          languageData.forEach((l: any) => {
            const rawId = l.id || l.code?.toLowerCase() || 'unknown';
            const normalizedKey = normalizeLangKey(rawId);
            if (!uniqueLanguagesMap.has(normalizedKey) && l.enabled !== false && l.is_active !== 0) {
              uniqueLanguagesMap.set(normalizedKey, {
                id: rawId,
                name: l.name || l.full_name || 'Unknown Language',
                full_name: l.full_name || l.name || 'Unknown Language',
                code: l.code || String(rawId).toUpperCase() || 'UN',
                flag: l.flag || l.avatar || (String(rawId).toLowerCase() === 'vn' || String(rawId).toLowerCase() === 'vi' ? 'vn' : String(rawId).toLowerCase() === 'en' ? 'us' : String(rawId).toLowerCase()),
                isDefault: l.is_default === 1 || l.isDefault === 1 || l.isDefault === true || l.is_default === true,
                enabled: true,
              });
            }
          });
          const sortedLangs = Array.from(uniqueLanguagesMap.values()).sort((a, b) => {
            const aDef = a.isDefault || a.id === 'vn' || a.id === 'vi';
            const bDef = b.isDefault || b.id === 'vn' || b.id === 'vi';
            if (aDef && !bDef) return -1;
            if (!aDef && bDef) return 1;
            return 0;
          });
          setProjectLanguages(sortedLangs);
        }
      } catch (error) {
        console.error('Error fetching project languages in GroupDetailModal:', error);
      } finally {
        if (isMounted) {
          setIsLoadingLanguages(false);
        }
      }
    };

    fetchProjectLanguages();

    return () => {
      isMounted = false;
    };
  }, [isOpen, idPath]);

  const languages = useMemo(() => {
    return localTranslations?.map((t: any) => ({
      id: t.language_id,
      name: t.language_name,
      code: t.language_code || t.code || t.lang_code || t.language_id,
      flag: t.flag
    })) || [];
  }, [localTranslations]);

  // Keep localTranslations in sync when prop or projectLanguages change
  useEffect(() => {
    const rawTranslations = (group?.translations || []) as GroupTranslation[];

    let mergedTranslations: GroupTranslation[] = [];
    if (projectLanguages.length > 0) {
      mergedTranslations = projectLanguages.map((pLang: any) => {
        const existing = rawTranslations.find((t: any) => isSameLanguage(t, pLang));
        if (existing) {
          return {
            ...existing,
            language_id: pLang.id,
            language_name: pLang.name || existing.language_name,
            language_full_name: pLang.full_name || existing.language_full_name || pLang.name,
            flag: pLang.flag || existing.flag,
            isDefault: pLang.isDefault
          };
        }
        return {
          language_id: pLang.id,
          language_name: pLang.name,
          language_full_name: pLang.full_name || pLang.name,
          title: '',
          information: '',
          transcript: '',
          flag: pLang.flag,
          isDefault: pLang.isDefault
        };
      });
    } else {
      mergedTranslations = rawTranslations;
    }

    // Deduplicate mergedTranslations by normalized language key
    const seenMap = new Map<string, GroupTranslation>();
    mergedTranslations.forEach(t => {
      const key = normalizeLangKey(t.language_id);
      if (!seenMap.has(key)) {
        seenMap.set(key, t);
      }
    });

    // Sort so that the default language is ALWAYS at the top (index 0)
    const finalTranslations = Array.from(seenMap.values()).sort((a, b) => {
      const aDef = (a as any).isDefault || a.language_id === 'vn' || a.language_id === 'vi';
      const bDef = (b as any).isDefault || b.language_id === 'vn' || b.language_id === 'vi';
      if (aDef && !bDef) return -1;
      if (!aDef && bDef) return 1;
      return 0;
    });

    setLocalTranslations(finalTranslations);

    const nextVoiceContents: { [key: string]: string } = {};
    finalTranslations.forEach((t) => {
      nextVoiceContents[t.language_id] = t.transcript || '';
    });
    setVoiceContents(nextVoiceContents);
    setVoiceUrls({});

    // Select default language tab by default
    if (finalTranslations.length > 0) {
      const defaultTrans = finalTranslations.find((t: any) => t.isDefault || t.language_id === 'vn' || t.language_id === 'vi');
      const defaultId = defaultTrans ? defaultTrans.language_id : finalTranslations[0].language_id;
      setActiveAudioLangId(prev => (finalTranslations.some(m => m.language_id === prev) ? prev : defaultId));
    }
  }, [group?.id, group?.translations, projectLanguages]);

  // Determine if there's any content in titles or informations (consider edited state)
  const hasAnyContent = useMemo(() => {
    const stripText = (html?: string) => (html ? html.replace(/<[^>]*>/g, '').trim() : '');
    if (!localTranslations || localTranslations.length === 0) return false;
    for (const t of localTranslations) {
      const edited = editedTranslations[t.language_id];
      const title = (edited?.title ?? t.title ?? '').trim();
      const info = stripText(edited?.information ?? t.information ?? '');
      if (title || info) return true;
    }
    return false;
  }, [localTranslations, editedTranslations]);

  // Validation: Allow empty information even if title exists
  const validatePair = (title?: string, information?: string) => {
    // We now allow information (content) to be empty even if title is present
    // However, if there is information, we still prefer having a title
    const t = (title || '').trim();
    const plainInfo = information ? information.replace(/<[^>]*>/g, '').trim() : '';
    
    // If there is info but no title, that might still be an issue, 
    // but the user's request is to "allow content to be empty".
    // Let's just return true to be fully lenient as requested.
    return true;
  };

  const validateTranslationsPayload = (payload: { language_id: string, title: string, information: string }[]) => {
    const invalid: string[] = [];
    payload.forEach(p => {
      if (!validatePair(p.title, p.information)) {
        const lang = localTranslations?.find((lt: any) => lt.language_id === p.language_id);
        invalid.push(lang?.language_name || p.language_id);
      }
    });
    return invalid;
  };

  const formatEstimateVnd = (value?: number | null) => {
    return `${Math.round(Number(value || 0)).toLocaleString('vi-VN')} VND`;
  };

  const resolveTranslationBudgetEstimate = (confirmed: boolean) => {
    translationBudgetResolverRef.current?.(confirmed);
    translationBudgetResolverRef.current = null;
    setTranslationBudgetEstimate(null);
  };

  const confirmBudgetEstimate = (estimate: TranslationBudgetEstimate) => {
    return new Promise<boolean>((resolve) => {
      translationBudgetResolverRef.current = resolve;
      setTranslationBudgetEstimate(estimate);
    });
  };

  const { isTranslating, translateAllFields, translateSingleField } = useAutoTranslate({
    languages,
    showSuccess: (message: string) => {
      // Use global toast for success
      try {
        globalUtils.general.showToast(message || 'Thành công', 'Success', 'success', true, 3000);
      } catch (e) {
      }
    },
    showError: (message: string) => {
      try {
        globalUtils.general.showToast(message || 'Lỗi', 'Error', 'error', true, 5000);
      } catch (e) {
        console.error('Error:', message);
      }
    },
    showWarning: (message: string) => {
      try {
        globalUtils.general.showToast(message || 'Cảnh báo', 'Warning', 'warning', true, 4000);
      } catch (e) {
        console.warn('Warning:', message);
      }
    },
    confirmBudgetEstimate
  });

  // Adapter setters for useAutoTranslate to merge results into `editedTranslations` state
  const setTitlesFromTranslate = (dataOrUpdater: any) => {
    if (typeof dataOrUpdater === 'function') {
      // build current titles map
      const currentTitles: { [k: string]: string } = {};
      (localTranslations || []).forEach((t: any) => {
        currentTitles[t.language_id] = editedTranslations[t.language_id]?.title ?? t.title ?? '';
      });
      const newTitles = dataOrUpdater(currentTitles);
      setEditedTranslations(prev => {
        const next = { ...prev };
        Object.keys(newTitles || {}).forEach(lang => {
          next[lang] = { title: newTitles[lang] ?? '', information: (next[lang]?.information ?? '') };
        });
        return next;
      });
    } else {
      const newTitles = dataOrUpdater || {};
      setEditedTranslations(prev => {
        const next = { ...prev };
        Object.keys(newTitles).forEach(lang => {
          next[lang] = { title: newTitles[lang] ?? '', information: (next[lang]?.information ?? '') };
        });
        return next;
      });
    }
  };

  const setInformationsFromTranslate = (dataOrUpdater: any) => {
    if (typeof dataOrUpdater === 'function') {
      const currentInfos: { [k: string]: string } = {};
      (localTranslations || []).forEach((t: any) => {
        currentInfos[t.language_id] = editedTranslations[t.language_id]?.information ?? t.information ?? '';
      });
      const newInfos = dataOrUpdater(currentInfos);
      setEditedTranslations(prev => {
        const next = { ...prev };
        Object.keys(newInfos || {}).forEach(lang => {
          next[lang] = { title: (next[lang]?.title ?? ''), information: newInfos[lang] ?? '' };
        });
        return next;
      });
    } else {
      const newInfos = dataOrUpdater || {};
      setEditedTranslations(prev => {
        const next = { ...prev };
        Object.keys(newInfos).forEach(lang => {
          next[lang] = { title: (next[lang]?.title ?? ''), information: newInfos[lang] ?? '' };
        });
        return next;
      });
    }
  };

  const getPlainText = (value?: string | null) => {
    return value ? value.replace(/<[^>]*>/g, '').trim() : '';
  };

  const hasAnyFieldContent = (fieldMap: { [k: string]: string }, stripHtmlContent = false) => {
    return (localTranslations || []).some((t: any) => {
      const value = fieldMap[t.language_id] || '';
      return stripHtmlContent ? Boolean(getPlainText(value)) : Boolean(value.trim());
    });
  };

  const hasEmptyTargetField = (fieldMap: { [k: string]: string }, stripHtmlContent = false) => {
    return (localTranslations || []).some((t: any) => {
      const value = fieldMap[t.language_id] || '';
      return stripHtmlContent ? !getPlainText(value) : !value.trim();
    });
  };

  // Translate all titles and informations (two passes)
  const handleTranslateAllTranslations = async (onlyEmptyFields = true) => {
    try {
      // Prepare title map
      const titleMap: { [k: string]: string } = {};
      const infoMap: { [k: string]: string } = {};
      (localTranslations || []).forEach((t: any) => {
        titleMap[t.language_id] = editedTranslations[t.language_id]?.title ?? t.title ?? '';
        infoMap[t.language_id] = editedTranslations[t.language_id]?.information ?? t.information ?? '';
      });

      const shouldTranslateTitles = hasAnyFieldContent(titleMap) && (!onlyEmptyFields || hasEmptyTargetField(titleMap));
      const shouldTranslateInformations = hasAnyFieldContent(infoMap, true) && (!onlyEmptyFields || hasEmptyTargetField(infoMap, true));

      if (!shouldTranslateTitles && !shouldTranslateInformations) {
        try {
          globalUtils.general.showToast('Chua co noi dung can dich hoac cac o can dich da co noi dung', 'Warning', 'warning', true, 4000);
        } catch (e) {
        }
        return;
      }

      if (shouldTranslateTitles) {
        // Translate titles (no HTML)
        const translatedTitles = await translateAllFields(titleMap, setTitlesFromTranslate, 'tiêu đề', false, onlyEmptyFields);
        if (!translatedTitles) return;
      }

      if (shouldTranslateInformations) {
        // Translate informations (preserve HTML/styling when possible)
        const translatedInformations = await translateAllFields(infoMap, setInformationsFromTranslate, 'thông tin', false, onlyEmptyFields);
        if (!translatedInformations) return;
      }

      try {
        globalUtils.general.showToast('Dịch tự động hoàn tất cho tất cả ngôn ngữ', 'Success', 'success', true, 3000);
      } catch (e) {
      }
    } catch (error) {
      console.error('Error translating all translations:', error);
    }
  };

  // Translate a single language (both title and information)
  const handleTranslateSingleLanguage = async (languageId: string) => {
    try {
      const titleMap: { [k: string]: string } = {};
      const infoMap: { [k: string]: string } = {};
      (localTranslations || []).forEach((t: any) => {
        titleMap[t.language_id] = editedTranslations[t.language_id]?.title ?? t.title ?? '';
        infoMap[t.language_id] = editedTranslations[t.language_id]?.information ?? t.information ?? '';
      });

      // Translate title for target language
      const translatedTitle = await translateSingleField(titleMap, setTitlesFromTranslate, languageId, 'title', 'tiêu đề', false, null);
      if (!translatedTitle) return;

      // Translate information for target language (preserve HTML/styling when possible)
      const translatedInformation = await translateSingleField(infoMap, setInformationsFromTranslate, languageId, 'information', 'thông tin', false, null);
      if (!translatedInformation) return;

      try {
        globalUtils.general.showToast(`Dịch tự động hoàn tất cho ${languageId}`, 'Success', 'success', true, 3000);
      } catch (e) {
      }
    } catch (error) {
      console.error('Error translating single language:', error);
    }
  };

  // Initialize selected voices with default values when languages change
  useEffect(() => {
    if (languages.length === 0) return;

    setSelectedVoices(prevSelectedVoices => {
      let changesMade = false;
      const updatedSelectedVoices: { [key: string]: { nam: string, nu: string } } = { ...prevSelectedVoices };

      languages.forEach(lang => {
        if (!updatedSelectedVoices[lang.id]) {
          updatedSelectedVoices[lang.id] = {
            nam: getVoiceFromPopup(lang.code, 'nam'),
            nu: getVoiceFromPopup(lang.code, 'nu')
          };
          changesMade = true;
        }
      });

      return changesMade ? updatedSelectedVoices : prevSelectedVoices;
    });
  }, [languages]);

  // Cleanup audio when component unmounts
  React.useEffect(() => {
    return () => {
      // Stop all playing audio when component unmounts
      const audioElements = document.querySelectorAll('audio');
      audioElements.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
    };
  }, []);

  // Initialize edited translations when editing starts
  const handleStartEdit = (languageId: string, translation: any) => {
    setEditingTranslation(languageId);
    setEditedTranslations(prev => ({
      ...prev,
      [languageId]: {
        title: translation.title || '',
        information: translation.information || ''
      }
    }));
  };

  // Handle title change
  const handleTitleChange = (languageId: string, title: string) => {
    setEditedTranslations(prev => ({
      ...prev,
      [languageId]: {
        ...prev[languageId],
        title
      }
    }));
  };

  // Handle information change
  const handleInformationChange = (languageId: string, information: string) => {
    setEditedTranslations(prev => ({
      ...prev,
      [languageId]: {
        ...prev[languageId],
        information
      }
    }));
  };

  // Save translation
  const handleSaveTranslation = async (id_path: string, languageId: string) => {
    try {
      setIsLoading(true);

      const editedData = editedTranslations[languageId];
      if (!editedData) return;

      // Validate pair: require both title and information (or both empty)
      if (!validatePair(editedData.title, editedData.information)) {
        const lang = localTranslations?.find((t: any) => t.language_id === languageId);
        const langName = lang?.language_name || languageId;
        try {
          globalUtils.general.showToast(`Yêu cầu có đủ tiêu đề và nộ dung cho ${langName}`, 'Warning', 'warning', true, 4000);
        } catch (e) {
          alert(`Yêu cầu có đủ tiêu đề và nộ dung cho ${langName}`);
        }
        return;
      }

      // Here you would call your API to save the translation

      const response = await groupSceneServices.updateGroup({
        id_path: id_path,
        group_id: group.id,
        body: {
          translations: [
            {
              language_id: languageId,
              title: editedData.title,
              information: editedData.information
            }
          ]
        }
      });
      // Mock API call - replace with actual API call
      // await new Promise(resolve => setTimeout(resolve, 1000));
      if (!response || !response.success) {
        try {
          globalUtils.general.showToast('Có lỗi xảy ra khi lưu!', 'Error', 'error', true, 5000);
        } catch (e) {
          alert('Có lỗi xảy ra khi lưu!');
        }
        return;
      }
      // Reset editing state
      // Update local translations so UI reflects saved data immediately
      setLocalTranslations(prev => prev.map(t => t.language_id === languageId ? ({
        ...t,
        title: editedData.title !== undefined ? editedData.title : t.title,
        information: editedData.information !== undefined ? editedData.information : t.information
      }) : t));

      setEditingTranslation(null);
      setEditedTranslations(prev => {
        const newState = { ...prev };
        delete newState[languageId];
        return newState;
      });

      // Refresh group data to show updated translations
      if (onRefresh) {
        onRefresh();
      }

      try {
        globalUtils.general.showToast('Lưu thành công!', 'Success', 'success', true, 3000);
      } catch (e) {
        alert('Lưu thành công!');
      }

    } catch (error) {
      console.error('Error saving translation:', error);
      alert('Có lỗi xảy ra khi lưu!');
    } finally {
      setIsLoading(false);
    }
  };

  // Save all translations at once
  const handleSaveAllTranslations = async (id_path: string) => {
    try {
      setIsLoading(true);

      if (!localTranslations || localTranslations.length === 0 || !hasAnyContent) {
        try {
          globalUtils.general.showToast('Không có bản dịch để lưu', 'Warning', 'warning', true, 3000);
        } catch (e) {
          alert('Không có bản dịch để lưu');
        }
        return;
      }

      const translationsPayload = (localTranslations || [])
        .map((t: any) => {
          const edited = editedTranslations[t.language_id];
          return {
            language_id: t.language_id,
            title: edited && edited.title !== undefined ? edited.title : (t.title || ''),
            information: edited && edited.information !== undefined ? edited.information : (t.information || '')
          };
        })
        .filter((t: any) => {
          const hadPriorTranslation = (group?.translations || []).some((gt: any) => isSameLanguage(gt, { id: t.language_id, language_id: t.language_id }));
          return hadPriorTranslation || Boolean(t.title?.trim() || t.information?.trim());
        });

      // Validate payload: require title+info pair when one exists
      const invalidLangs = validateTranslationsPayload(translationsPayload);
      if (invalidLangs.length > 0) {
        const list = invalidLangs.join(', ');
        try {
          globalUtils.general.showToast(`Vui lòng nhập cả tiêu đề và nội dung cho: ${list}`, 'Warning', 'warning', true, 5000);
        } catch (e) {
          alert(`Vui lòng nhập cả tiêu đề và nội dung cho: ${list}`);
        }
        return;
      }

      const response = await groupSceneServices.updateGroup({
        id_path,
        group_id: group.id,
        body: { translations: translationsPayload }
      });

      if (!response || !response.success) {
        try {
          globalUtils.general.showToast('Có lỗi xảy ra khi lưu toàn bộ bản dịch!', 'Error', 'error', true, 5000);
        } catch (e) {
          alert('Có lỗi xảy ra khi lưu toàn bộ bản dịch!');
        }
        return;
      }

      // Update local translations so UI reflects saved data immediately
      const updatedLocal = (localTranslations || []).map((t: any) => {
        const edited = editedTranslations[t.language_id];
        return {
          ...t,
          title: edited && edited.title !== undefined ? edited.title : (t.title || ''),
          information: edited && edited.information !== undefined ? edited.information : (t.information || '')
        };
      });
      setLocalTranslations(updatedLocal);

      // Clear edited state
      setEditedTranslations({});

      if (onRefresh) onRefresh();

      try {
        globalUtils.general.showToast('Lưu thành công tất cả bản dịch!', 'Success', 'success', true, 3000);
      } catch (e) {
        alert('✅ Lưu thành công tất cả bản dịch!');
      }
    } catch (error) {
      console.error('Error saving all translations:', error);
      alert('Có lỗi xảy ra khi lưu toàn bộ bản dịch!');
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel editing
  const handleCancelEdit = (languageId: string) => {
    setEditingTranslation(null);
    setEditedTranslations(prev => {
      const newState = { ...prev };
      delete newState[languageId];
      return newState;
    });
  };

  const pendingVoiceCount = useMemo(() => {
    return (localTranslations || []).reduce((count: number, translation: GroupTranslation) => {
      const languageId = translation.language_id;
      const languageVoices = voiceUrls[languageId] || {};
      const transcript = (voiceContents[languageId] || '').trim();
      const transcriptChanged = transcript !== (translation.transcript || '').trim();

      (['male', 'female'] as const).forEach(genderKey => {
        const touched = Object.prototype.hasOwnProperty.call(languageVoices, genderKey);
        const existingVoice = getGroupVoice(group?.voices, languageId, genderKey);
        if (touched || (transcriptChanged && existingVoice)) {
          count += 1;
        }
      });

      return count;
    }, 0);
  }, [group?.voices, localTranslations, voiceContents, voiceUrls]);

  // Handle voice update
  const handleUpdateVoices = async (id_path: string) => {
    try {
      setIsLoading(true);

      const voices: { language_id: string; gender: string; audio_path: string | null; transcript?: string; mode?: string }[] = [];

      (localTranslations || []).forEach((translation: GroupTranslation) => {
        const languageId = translation.language_id;
        const languageVoices = voiceUrls[languageId] || {};
        const transcript = voiceContents[languageId] || '';
        const transcriptChanged = transcript.trim() !== (translation.transcript || '').trim();

        (['male', 'female'] as const).forEach(genderKey => {
          const apiGender = toApiGender(genderKey);
          const touched = Object.prototype.hasOwnProperty.call(languageVoices, genderKey);
          const existingVoice = getGroupVoice(group?.voices, languageId, genderKey);
          const audioPath = touched
            ? (languageVoices[genderKey]?.trim() || null)
            : (existingVoice?.audio_path || null);

          if ((touched && (audioPath !== null || existingVoice)) || (transcriptChanged && existingVoice)) {
            voices.push({
              language_id: languageId,
              gender: apiGender,
              audio_path: audioPath,
              transcript,
              mode: 'group'
            });
          }
        });
      });

      if (voices.length === 0) {
        alert('Không có thay đổi voice nào để cập nhật!');
        return;
      }

      // Call API to update voices
      const response = await groupSceneServices.addVoicesToGroup({
        id_path: id_path,
        group_id: group.id,
        body: { voices }
      });

      if (!response || !response.success) {
        alert('Có lỗi xảy ra khi cập nhật voices!');
        return;
      }

      // Clear voice URLs after successful update
      setVoiceUrls({});

      // Create detailed success message
      const voicesByLanguage: { [key: string]: { updated: string[], deleted: string[] } } = {};
      voices.forEach(voice => {
        const langName = localTranslations?.find((t: GroupTranslation) => t.language_id === voice.language_id)?.language_name || voice.language_id;
        if (!voicesByLanguage[langName]) voicesByLanguage[langName] = { updated: [], deleted: [] };
        const genderLabel = voice.gender === 'nam' ? 'Nam' : 'Nữ';
        if (voice.audio_path === null) {
          voicesByLanguage[langName].deleted.push(genderLabel);
        } else {
          voicesByLanguage[langName].updated.push(genderLabel);
        }
      });

      const detailLines: string[] = [];
      Object.entries(voicesByLanguage).forEach(([lang, info]) => {
        if (info.updated.length > 0) {
          detailLines.push(`${lang}: Cập nhật ${info.updated.join(', ')}`);
        }
        if (info.deleted.length > 0) {
          detailLines.push(`${lang}: Xóa ${info.deleted.join(', ')}`);
        }
      });

      alert(`✅ Đã xử lý ${voices.length} voice thành công!\n\nChi tiết:\n${detailLines.join('\n')}`);

      // Refresh group data to show updated voices
      if (onRefresh) {
        onRefresh();
      }

    } catch (error) {
      console.error('Error updating voices:', error);
      alert('Có lỗi xảy ra khi cập nhật voices!');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle voice URL changes
  const handleVoiceUrlChange = (languageId: string, gender: 'male' | 'female', url: string) => {
    setVoiceUrls(prev => ({
      ...prev,
      [languageId]: {
        ...prev[languageId],
        [gender]: url
      }
    }));
  };

  // Get voice URL for display
  // Use !== undefined check instead of || to allow empty string (user clearing the input)
  const getVoiceUrl = (languageId: string, gender: 'male' | 'female', defaultVoice?: any) => {
    const userValue = voiceUrls[languageId]?.[gender];
    // If user has entered any value (including empty string), use it
    // Otherwise fallback to defaultVoice
    if (userValue !== undefined) {
      return userValue;
    }
    return defaultVoice?.audio_path || '';
  };

  const handleVoiceContentChange = (languageId: string, content: string) => {
    setVoiceContents(prev => ({
      ...prev,
      [languageId]: content
    }));
  };

  // Handle translate all voice contents
  const handleTranslateAllVoiceContents = async () => {
    await translateAllFields(
      voiceContents,
      setVoiceContents,
      'nội dung voice',
      true, // Strip HTML since this is for voice content
      true // onlyEmptyFields
    );
  };

  // Handle translate single voice content
  const handleTranslateSingleVoiceContent = async (languageId: string) => {
    await translateSingleField(
      voiceContents,
      setVoiceContents,
      languageId,
      'voice_content',
      'nội dung voice',
      true // Strip HTML since this is for voice content
    );
  };

  // Handle voice selection from popup
  const handleVoiceSelect = (languageId: string, gender: 'nam' | 'nu', voiceCode: string) => {
    setSelectedVoices(prev => ({
      ...prev,
      [languageId]: {
        ...prev[languageId],
        [gender]: voiceCode
      }
    }));
  };

  // Apply default voices for a language
  const handleApplyDefaultVoices = (languageId: string) => {
    const lang = languages.find((l: any) => l.id === languageId);
    if (!lang) return;

    setSelectedVoices(prev => ({
      ...prev,
      [languageId]: {
        nam: getVoiceFromPopup(lang.code, 'nam'),
        nu: getVoiceFromPopup(lang.code, 'nu')
      }
    }));
  };

  // Handle open voice popup for specific language and gender
  const handleOpenVoicePopup = (langId: string, gender: 'male' | 'female') => {
    setShowVoicePopup({ langId, gender: gender === 'male' ? 'nam' : 'nu' });
  };

  // Handle close voice popup
  const handleCloseVoicePopup = () => {
    setShowVoicePopup(null);
  };

  // Clear both voices for a language
  const handleClearLanguageVoices = (langId: string) => {
    setVoiceUrls(prev => ({
      ...prev,
      [langId]: { male: '', female: '' }
    }));
  };

  // Clear all voices across all languages
  const handleClearAllVoices = () => {
    const cleared: { [key: string]: { male: string; female: string } } = {};
    localTranslations?.forEach((t: any) => {
      cleared[t.language_id] = { male: '', female: '' };
    });
    setVoiceUrls(cleared);
  };

  if (!isOpen || !group) return null;


  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      {translationBudgetEstimate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[70] transition-all duration-300">
          <div className="bg-white/95 rounded-2xl w-full max-w-2xl p-6 space-y-6 shadow-2xl border border-gray-100 flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h2 className="text-xl font-bold text-gray-900 font-['Roboto_Flex'] flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-500" />
                ƯỚC LƯỢNG CHI PHÍ DỊCH THUẬT
              </h2>
              <button
                onClick={() => resolveTranslationBudgetEstimate(false)}
                className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto pr-1 flex-1">
              <p className="text-sm text-gray-600 font-medium leading-relaxed">
                Hệ thống sẽ dịch tự động phần <strong className="text-blue-600">{translationBudgetEstimate.label}</strong> theo dữ liệu hiện có trong chi tiết scene.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50/50 border border-blue-100/50 p-4 rounded-xl space-y-1">
                  <span className="text-xs text-blue-600 font-bold uppercase tracking-wider">Ký tự cần dịch</span>
                  <p className="text-2xl font-black text-blue-900">
                    {translationBudgetEstimate.billableCharacterCount.toLocaleString('vi-VN')}
                    <span className="text-xs text-gray-500 font-medium block">
                      {translationBudgetEstimate.targetCount} ngôn ngữ đích
                    </span>
                  </p>
                </div>
                <div className="bg-green-50/50 border border-green-100/50 p-4 rounded-xl space-y-1">
                  <span className="text-xs text-green-600 font-bold uppercase tracking-wider">Chi phí lượt này</span>
                  <p className="text-2xl font-black text-green-900">
                    {formatEstimateVnd(translationBudgetEstimate.estimatedCostVnd)}
                    <span className="text-xs text-gray-500 font-medium block">
                      (~ ${translationBudgetEstimate.estimatedCostUsd.toFixed(6)} USD)
                    </span>
                  </p>
                </div>
              </div>

              <div className={`border p-4 rounded-xl text-sm ${
                translationBudgetEstimate.canExecute
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span className="font-bold">Ngân sách dịch của dự án</span>
                  <span className="rounded-full bg-white/80 px-2 py-0.5 text-xs font-bold">
                    {translationBudgetEstimate.canExecute ? 'Đủ ngân sách' : 'Thiếu ngân sách'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>Đã cấp: <strong>{translationBudgetEstimate.budget?.total_budget_vnd === null ? 'Chưa cấp' : formatEstimateVnd(translationBudgetEstimate.budget?.total_budget_vnd)}</strong></div>
                  <div>Đã dùng: <strong>{formatEstimateVnd(translationBudgetEstimate.budget?.used_amount_vnd)}</strong></div>
                  <div>Còn lại: <strong>{translationBudgetEstimate.hasBudget ? formatEstimateVnd(translationBudgetEstimate.budget?.remaining_budget_vnd) : 'Chưa cấp ngân sách'}</strong></div>
                  <div>Sau dịch: <strong>{translationBudgetEstimate.hasBudget ? formatEstimateVnd(Number(translationBudgetEstimate.budget?.remaining_budget_vnd || 0) - Number(translationBudgetEstimate.estimatedCostVnd || 0)) : 'Không khả dụng'}</strong></div>
                </div>
              </div>

            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
              <button
                onClick={() => resolveTranslationBudgetEstimate(false)}
                className="px-5 py-2.5 bg-gray-100 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-200 transition-colors"
              >
                Hủy bỏ
              </button>
              <button
                onClick={() => resolveTranslationBudgetEstimate(true)}
                disabled={translationBudgetEstimate.canExecute === false}
                className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/20 flex items-center gap-2 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
              >
                <Globe className="w-4 h-4" />
                Đồng ý dịch
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden">
        <div className="flex h-[95vh]">
          {/* Left Panel - Group Info */}
          <div className="w-1/3 border-r bg-gray-50 p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="space-y-2">
                <h2 className="text-xl font-semibold">
                  Chi tiết nhóm
                </h2>
                <QrCodeBadge entityType="group" entityId={group.id} />
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Thumbnail */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Thumbnail</label>
                <div className="aspect-video rounded-lg overflow-hidden bg-gray-200">
                  <img
                    src={group.thumbnail || ""}
                    alt="Group thumbnail"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = "";
                    }}
                  />
                </div>
              </div>

              {/* Basic Info */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500">ID</label>
                    <div className="flex items-center gap-2">
                      <Hash className="h-3 w-3 text-gray-500" />
                      <span className="text-sm font-mono">{group.id}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Index</label>
                    <div className="text-sm">{group.index}</div>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500">UUID</label>
                  <div className="text-sm font-mono bg-gray-100 p-2 rounded text-xs break-all">
                    {group.group_uuid}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500">Version ID</label>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-600">
                      {group.version_id}
                    </span>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Hiển thị</label>
                    <div className="flex items-center gap-2">
                      {group.is_show ? (
                        <Eye className="h-4 w-4 text-green-500" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      )}
                      <span className="text-sm">{group.is_show ? "Đang hiển thị" : "Đã ẩn"}</span>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  {/* Parent Info */}
                  {group.parent && (
                    <div>
                      <label className="text-xs text-gray-500">Nhóm cha</label>
                      <div className="flex items-center gap-2 p-2 bg-gray-100 rounded">
                        <Folder className="h-4 w-4 text-blue-600" />
                        <span className="text-sm">
                          {group.parent.translations?.find((t: any) => t.language_id === "vn")?.title || "Không có tên"}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Timestamps */}
                  <div className="space-y-2 mt-4">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Calendar className="h-3 w-3" />
                      <span>Tạo: {new Date(group.created_at).toLocaleString("vi-VN")}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <User className="h-3 w-3" />
                      <span>Bởi: User {group.created_by}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Tabs Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            {/* Tab Navigation */}
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6">
              <button
                onClick={() => setActiveTab('translations')}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-2 ${activeTab === 'translations'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <Globe className="h-4 w-4" />
                Bản dịch
              </button>
              <button
                onClick={() => setActiveTab('audio')}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-2 ${activeTab === 'audio'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <Volume2 className="h-4 w-4" />
                Audio & Nội dung thuyết minh
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-2 ${activeTab === 'settings'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <Settings className="h-4 w-4" />
                Cài đặt
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'translations' && (
              <div className="space-y-6">


                {/* Global actions: Translate All + Save All (visible - not hidden behind menus) */}
                <div className="flex justify-end items-center gap-3">
                  <button
                    onClick={() => handleTranslateAllTranslations(true)}
                    disabled={isTranslating || !hasAnyContent}
                    className={`px-4 py-2 text-sm rounded-lg font-medium flex items-center gap-2 ${isTranslating || !hasAnyContent ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'}`}
                    title="Dịch tự động cho tất cả ngôn ngữ (chỉ các ô trống)"
                  >
                    {isTranslating ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        Đang dịch...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 0 1 6.412 9m6.088 9h7" /></svg>
                        Dịch tự động
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => handleSaveAllTranslations(idPath)}
                    disabled={isLoading || (Object.keys(editedTranslations).length === 0 && !hasAnyContent)}
                    className={`px-4 py-2 text-sm rounded-lg font-medium flex items-center gap-2 ${isLoading || (Object.keys(editedTranslations).length === 0 && !hasAnyContent) ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                    title="Lưu tất cả bản dịch"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        Đang lưu...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        Lưu tất cả
                      </>
                    )}
                  </button>
                </div>

                {/* Translation List */}
                {localTranslations && localTranslations.length > 0 ? (
                  <div className="space-y-4">
                    {localTranslations.map((translation: any) => {
                      // Always allow editing without requiring the user to press an Edit button
                      const isEditing = true;
                      const editedData = editedTranslations[translation.language_id];

                      return (
                        <div key={translation.language_id} className={`border rounded-lg p-6 transition-all ${isEditing ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200'}`}>
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                                {translation.language_name}
                              </span>
                              <span className="text-sm text-gray-600">{translation.language_full_name}</span>
                              {/* {isEditing && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                  Đang chỉnh sửa
                                </span>
                              )} */}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleTranslateSingleLanguage(translation.language_id)}
                                disabled={isTranslating || !hasAnyContent}
                                className="flex items-center gap-2 px-3 py-2 bg-yellow-100 text-yellow-800 rounded-md hover:bg-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                title={`Dịch tự động cho ${translation.language_name}`}
                              >
                                {isTranslating ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-800"></div>
                                ) : (
                                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 0 1 6.412 9m6.088 9h7" /></svg>
                                )}
                                <span className="text-sm">Dịch</span>
                              </button>
                            </div>
                          </div>

                          <div className="space-y-6">
                            {/* Title Section */}
                            <div>
                              <label className="text-sm font-semibold mb-3 block text-gray-700">
                                📝 Tiêu đề nhóm
                              </label>
                              {isEditing ? (
                                <input
                                  value={editedData?.title ?? translation.title ?? ''}
                                  onChange={(e) => handleTitleChange(translation.language_id, e.target.value)}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                                  placeholder="Nhập tiêu đề nhóm..."
                                />
                              ) : (
                                <div className="text-lg font-medium p-4 bg-gray-50 rounded-lg border">
                                  {translation.title || <span className="text-gray-400 italic">Chưa có tiêu đề</span>}
                                </div>
                              )}
                            </div>

                            {/* Information Section */}
                            <div>
                              <label className="text-sm font-semibold mb-3 block text-gray-700">
                                📄 Thông tin chi tiết
                              </label>
                              {isEditing ? (
                                <div className="space-y-3">
                                  <CKEditorWrapper
                                    data={editedData?.information ?? translation.information ?? ''}
                                    onChange={(data) => handleInformationChange(translation.language_id, data)}
                                    height="200px" // hoặc '300px' tùy bạn
                                    toolbar={[
                                      'heading',
                                      '|',
                                      'bold',
                                      'italic',
                                      'underline',
                                      'link',
                                      '|',
                                      'bulletedList',
                                      'numberedList',
                                      '|',
                                      'alignment',
                                      '|',
                                      'blockQuote',
                                      'insertTable',
                                      'imageUpload',
                                      '|',
                                      'outdent',
                                      'indent',
                                      '|',
                                      'blockQuote',
                                      'insertTable',
                                      'undo',
                                      'redo',
                                    ]}
                                    onImageUpload={handleEditorImageUpload}
                                  />
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    <div className="text-xs text-blue-700 flex items-center gap-2">
                                      <span>💡</span>
                                      <span className="font-medium">Hướng dẫn sử dụng Enhanced Editor:</span>
                                    </div>
                                    <ul className="text-xs text-blue-600 mt-1 ml-4 space-y-1">
                                      <li>• Sử dụng toolbar để định dạng văn bản (in đậm, nghiêng, danh sách...)</li>
                                      <li>• Có thể chèn bảng, hình ảnh, liên kết, trích dẫn và code blocks</li>
                                      <li>• Hỗ trợ các tiêu đề từ H1 đến H4 và căn chỉnh văn bản</li>
                                      <li>• Nội dung sẽ được lưu dưới dạng HTML</li>
                                    </ul>
                                  </div>
                                </div>
                              ) : (
                                <div className="min-h-[200px] p-4 bg-gray-50 rounded-lg text-base leading-relaxed overflow-y-auto border">
                                  <MemoizedHtmlRenderer html={translation.information} />
                                </div>
                              )}
                            </div>

                            {/* Metadata & Stats */}
                            {!isEditing && (
                              <div className="border-t pt-4">
                                <div className="grid grid-cols-3 gap-4 text-sm">
                                  <div className="bg-blue-50 p-3 rounded-lg">
                                    <div className="text-blue-600 font-medium">Độ dài tiêu đề</div>
                                    <div className="text-blue-800 text-xl font-bold">{translation.title?.length || 0}</div>
                                    <div className="text-blue-500 text-xs">ký tự</div>
                                  </div>
                                  <div className="bg-green-50 p-3 rounded-lg">
                                    <div className="text-green-600 font-medium">Độ dài nội dung</div>
                                    <div className="text-green-800 text-xl font-bold">{translation.information?.replace(/<[^>]*>/g, '')?.length || 0}</div>
                                    <div className="text-green-500 text-xs">ký tự</div>
                                  </div>
                                  <div className="bg-purple-50 p-3 rounded-lg">
                                    <div className="text-purple-600 font-medium">Trạng thái</div>
                                    <div className="text-purple-800 text-lg font-bold">
                                      {translation.title && translation.information ? 'Hoàn tất' : 'Chưa đầy đủ'}
                                    </div>
                                    <div className="text-purple-500 text-xs">
                                      {translation.title && translation.information ? '✅' : '⚠️'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Empty State - No Translations */
                  <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <Globe className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Chưa có bản dịch nào</h3>
                    <p className="text-gray-500 mb-6 max-w-md mx-auto">
                      Nhóm này chưa có bản dịch cho bất kỳ ngôn ngữ nào. Bản dịch sẽ được thêm tự động từ hệ thống.
                    </p>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-lg mx-auto">
                      <div className="text-sm text-yellow-800">
                        <strong>Lưu ý:</strong> Nếu bạn thấy thông báo này, có thể:
                        <ul className="mt-2 text-left space-y-1">
                          <li>• Dữ liệu chưa được tải từ API</li>
                          <li>• Nhóm này chưa có bản dịch trong hệ thống</li>
                          <li>• Có lỗi khi lấy dữ liệu bản dịch</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Add Translation Button - Optional */}
                {localTranslations && localTranslations.length > 0 && (
                  <div className="border-t pt-6">
                    <button className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2">
                      <Globe className="h-4 w-4" />
                      Thêm bản dịch ngôn ngữ mới
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'audio' && (
              <div className="space-y-6">
                {/* Header with Update Button */}
                <div className="flex items-center justify-between">
                  <div className="border rounded-lg p-6 flex-1">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Mic className="h-5 w-5" />
                      Quản lý Audio theo ngôn ngữ
                    </h3>
                  </div>

                  {/* Update Voices Button */}
                  <div className="ml-4">
                    <button
                      onClick={() => handleUpdateVoices(idPath)}
                      disabled={isLoading || pendingVoiceCount === 0}
                      className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${isLoading || pendingVoiceCount === 0
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
                        }`}
                      title={pendingVoiceCount === 0 ? 'Không có voice nào cần cập nhật' : 'Cập nhật voices nhóm cảnh'}
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Đang cập nhật...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          Cập nhật Voices ({pendingVoiceCount})
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  {/* Toolbar */}
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      <TranslateButton
                        onClick={handleTranslateAllVoiceContents}
                        isTranslating={isTranslating}
                        buttonText="Dịch tất cả nội dung"
                        title="Tự động dịch nội dung voice sang tất cả ngôn ngữ"
                        variant="green"
                        size="medium"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm('Xóa TẤT CẢ voice của mọi ngôn ngữ? Thay đổi sẽ có hiệu lực khi bấm Cập nhật Voices.')) {
                            handleClearAllVoices();
                          }
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-all duration-200 text-sm"
                        title="Xóa tất cả voice của mọi ngôn ngữ"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Xóa tất cả
                      </button>
                    </div>
                  </div>

                  {/* Language Tabs */}
                  <div className="flex border-b border-gray-200 bg-gray-50/50 overflow-x-auto">
                    {localTranslations?.map((translation: any) => {
                      const isActive = translation.language_id === activeAudioLangId;
                      const hasContent = Boolean(voiceContents[translation.language_id]?.trim());
                      const mv = group.voices?.find((v: any) => isSameLanguage(v, { id: translation.language_id, code: translation.code, name: translation.language_name }) && v.gender === "nam");
                      const fv = group.voices?.find((v: any) => isSameLanguage(v, { id: translation.language_id, code: translation.code, name: translation.language_name }) && v.gender === "nu");
                      const hasMale = Boolean(getVoiceUrl(translation.language_id, 'male', mv));
                      const hasFemale = Boolean(getVoiceUrl(translation.language_id, 'female', fv));
                      return (
                        <button
                          key={translation.language_id}
                          type="button"
                          onClick={() => setActiveAudioLangId(translation.language_id)}
                          className={`relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                            isActive
                              ? 'border-blue-600 bg-white text-blue-700'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'
                          }`}>
                            {translation.language_name}
                          </span>
                          {translation.language_full_name && (
                            <span className="hidden sm:inline text-xs">{translation.language_full_name}</span>
                          )}
                          {/* Status dots */}
                          <span className="flex items-center gap-0.5 ml-1">
                            <span className={`w-1.5 h-1.5 rounded-full ${hasContent ? 'bg-green-500' : 'bg-gray-300'}`} title={hasContent ? 'Có nội dung' : 'Chưa có nội dung'} />
                            <span className={`w-1.5 h-1.5 rounded-full ${hasMale ? 'bg-blue-500' : 'bg-gray-300'}`} title={hasMale ? 'Có voice nam' : 'Chưa có voice nam'} />
                            <span className={`w-1.5 h-1.5 rounded-full ${hasFemale ? 'bg-pink-500' : 'bg-gray-300'}`} title={hasFemale ? 'Có voice nữ' : 'Chưa có voice nữ'} />
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Active language content */}
                  {(() => {
                    const translation = localTranslations?.find((t: any) => t.language_id === activeAudioLangId) || localTranslations?.[0];
                    if (!translation) return null;
                    const maleVoice = group.voices?.find((v: any) =>
                      isSameLanguage(v, { id: translation.language_id, code: translation.code, name: translation.language_name }) && v.gender === "nam"
                    );
                    const femaleVoice = group.voices?.find((v: any) =>
                      isSameLanguage(v, { id: translation.language_id, code: translation.code, name: translation.language_name }) && v.gender === "nu"
                    );
                    return (
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                              {translation.language_name}
                            </span>
                            {translation.language_full_name}
                          </h4>
                          <button
                            onClick={() => {
                              if (window.confirm(`Xóa cả 2 voice (nam + nữ) của ${translation.language_full_name}? Thay đổi có hiệu lực khi bấm Cập nhật Voices.`)) {
                                handleClearLanguageVoices(translation.language_id);
                              }
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-all duration-200 text-xs"
                            title={`Xóa tất cả voice của ${translation.language_full_name}`}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            <span>Xóa voice {translation.language_name}</span>
                          </button>
                        </div>

                        {/* Voice Content Section */}
                        <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-purple-800 flex items-center gap-2">
                              <Type className="h-4 w-4" />
                              Nội dung Voice — {translation.language_name}
                            </h4>
                            <button
                              type="button"
                              onClick={() => handleTranslateSingleVoiceContent(translation.language_id)}
                              disabled={isTranslating}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50 transition-colors"
                              title="Dịch tự động từ ngôn ngữ có sẵn"
                            >
                              {isTranslating ? (
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-700"></div>
                              ) : (
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 0 1 6.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                </svg>
                              )}
                              <span>Dịch</span>
                            </button>
                          </div>
                          <textarea
                            value={voiceContents[translation.language_id] || ''}
                            onChange={(e) => handleVoiceContentChange(translation.language_id, e.target.value)}
                            placeholder={`Nhập nội dung voice bằng ${translation.language_name}...`}
                            className="w-full px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-vertical bg-white"
                            rows={3}
                            disabled={isTranslating}
                          />
                          <div className="mt-2 text-xs text-purple-700">
                            💡 Nội dung này dùng để tạo một voice nghe thử chung, link chỉ được lưu khi chọn dùng cho Voice nam/nữ hoặc nhập trực tiếp.
                          </div>
                        </div>

                        <div>
                          <TTSGeneratorSection
                            idPath={idPath}
                            langCode={translation.language_id}
                            gender="nam"
                            transcript={voiceContents[translation.language_id] || ''}
                            savedAudioTargets={[
                              {
                                key: 'male',
                                label: 'Voice nam',
                                audioUrl: getVoiceUrl(translation.language_id, 'male', maleVoice),
                                onChangeAudioUrl: (newUrl) => handleVoiceUrlChange(translation.language_id, 'male', newUrl)
                              },
                              {
                                key: 'female',
                                label: 'Voice nữ',
                                audioUrl: getVoiceUrl(translation.language_id, 'female', femaleVoice),
                                onChangeAudioUrl: (newUrl) => handleVoiceUrlChange(translation.language_id, 'female', newUrl)
                              }
                            ]}
                            isLoading={isLoading}
                            setIsLoading={setIsLoading}
                            showSuccess={showSuccess}
                            showError={showError}
                            showWarning={showWarning}
                            s3UploadService={s3UploadService}
                            selectedVoiceCode={selectedVoices[translation.language_id]?.nam || getVoiceFromPopup(translation.language_id, 'nam')}
                            onOpenVoicePopup={() => handleOpenVoicePopup(translation.language_id, 'male')}
                            ttsConfig={ttsConfig}
                            onChangeTTSConfig={setTtsConfig}
                            systemVoices={ttsResources.systemVoices}
                            projectPresets={ttsResources.projectPresets}
                            geminiOptions={ttsResources.geminiOptions}
                            vieneuOptions={ttsResources.vieneuOptions}
                            onRefreshPresets={refreshProjectPresets}
                            showConfiguration={true}
                            buildTTSRequest={buildTTSRequest}
                          />

                          {/* Subtitle Cue Editor for this Group Voice */}
                          {group?.id && (() => {
                            const maleUrl = getVoiceUrl(translation.language_id, 'male', maleVoice);
                            const femaleUrl = getVoiceUrl(translation.language_id, 'female', femaleVoice);
                            if (!maleUrl && !femaleUrl) return null;

                            const hasNam = Boolean(maleUrl);
                            const hasNu = Boolean(femaleUrl);
                            const currentVariant = activeSubtitleVariants[translation.language_id] || (hasNu ? 'nu' : 'nam');
                            const activeAudioUrl = currentVariant === 'nam' ? maleUrl : femaleUrl;

                            return (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                {hasNam && hasNu && (
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-xs font-medium text-gray-600">Chọn voice tạo phụ đề:</span>
                                    <button
                                      type="button"
                                      onClick={() => setActiveSubtitleVariants(prev => ({ ...prev, [translation.language_id]: 'nam' }))}
                                      className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                                        currentVariant === 'nam'
                                          ? 'bg-blue-600 text-white font-medium shadow-sm'
                                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                      }`}
                                    >
                                      Voice nam
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setActiveSubtitleVariants(prev => ({ ...prev, [translation.language_id]: 'nu' }))}
                                      className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                                        currentVariant === 'nu'
                                          ? 'bg-blue-600 text-white font-medium shadow-sm'
                                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                      }`}
                                    >
                                      Voice nữ
                                    </button>
                                  </div>
                                )}
                                <SubtitleCueEditor
                                  idPath={idPath}
                                  entityType="group"
                                  entityId={group.id}
                                  languageId={translation.language_id}
                                  voiceVariant={currentVariant}
                                  audioUrl={activeAudioUrl}
                                  sourceText={voiceContents[translation.language_id] || translation.transcript || ''}
                                  onSwitchTab={(langId, variant) => {
                                    if (langId) setActiveAudioLangId(langId);
                                    if (variant === 'nam' || variant === 'nu') {
                                      setActiveSubtitleVariants(prev => ({ ...prev, [langId || translation.language_id]: variant }));
                                    }
                                  }}
                                />
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Instructions and Status */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="text-blue-600 text-lg">💡</div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-blue-800 mb-2">Hướng dẫn sử dụng:</h4>
                      <ul className="text-xs text-blue-700 space-y-1">
                        <li>• <strong>Tạo nghe thử:</strong> Bấm "Tạo nghe thử" trong bộ máy TTS để sinh audio và link tạm.</li>
                        <li>• <strong>Dùng link:</strong> Nghe kiểm tra, rồi bấm "Dùng cho Voice nam" hoặc "Dùng cho Voice nữ" để đưa link vào ô cần lưu.</li>
                        <li>• <strong>Lưu link S3:</strong> Có thể dán URL hoặc upload file vào ô Voice nam/nữ, rồi bấm "Cập nhật Voices".</li>
                        <li>• <strong>Không tải về máy:</strong> Voice nghe thử và voice đã upload chỉ phát để kiểm tra, không hiện nút tải xuống trên trình phát.</li>
                        <li>• <strong>Chọn Voice:</strong> Với Google TTS, dùng nút "Chọn voice" để chọn giọng cụ thể.</li>
                        <li>• Hệ thống hỗ trợ file: MP3, WAV, M4A (tối đa 50MB)</li>
                      </ul>

                      {Object.keys(voiceUrls).length > 0 && (
                        <div className="mt-3 p-2 bg-white rounded border border-blue-300">
                          <div className="text-xs text-blue-800 font-medium mb-1">Voices đang chờ cập nhật:</div>
                          {Object.keys(voiceUrls).map(langId => {
                            const voices = voiceUrls[langId];
                            const languageName = localTranslations?.find((t: any) => t.language_id === langId)?.language_name || langId;
                            return (
                              <div key={langId} className="text-xs text-blue-700">
                                • {languageName}: {voices.male?.trim() ? 'Nam' : ''} {voices.male?.trim() && voices.female?.trim() ? ', ' : ''} {voices.female?.trim() ? 'Nữ' : ''}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 z-10 rounded-lg border border-blue-200 bg-white/95 p-4 shadow-sm backdrop-blur">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="text-sm font-semibold text-gray-800">Lưu voice nhóm cảnh</div>
                      <p className="mt-1 text-xs text-gray-500">
                        Link nghe thử hoặc link upload S3 chỉ được lưu vào nhóm sau khi bấm Cập nhật Voices.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleUpdateVoices(idPath)}
                      disabled={isLoading || pendingVoiceCount === 0}
                      className={`inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium transition-all ${isLoading || pendingVoiceCount === 0
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
                        }`}
                      title={pendingVoiceCount === 0 ? 'Không có voice nào cần cập nhật' : 'Cập nhật voices nhóm cảnh'}
                    >
                      {isLoading ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                          Đang cập nhật...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          Cập nhật Voices ({pendingVoiceCount})
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="border rounded-lg p-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium mb-4">Cài đặt nhóm</h3>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium">Hiển thị nhóm</label>
                          <p className="text-xs text-gray-500">
                            Cho phép nhóm này hiển thị trong danh sách
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={group.is_show === 1}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          readOnly
                        />
                      </div>

                      {/* Mouse Parallax Settings */}
                      <div className="border rounded-lg p-4 bg-gradient-to-br from-blue-50 to-indigo-50">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-blue-900">Mouse Parallax Effect</h4>
                            <p className="text-xs text-blue-600">View 360° di chuyển theo con trỏ chuột</p>
                          </div>
                        </div>

                        <ParallaxSettingsPanel
                          groupId={group.id}
                          initialEnabled={group.mouse_parallax === 1}
                          initialSensitivity={group.mouse_parallax_sensitivity ?? 10}
                          idPath={idPath}
                          onRefresh={onRefresh}
                        />
                      </div>

                      <div className="border-t pt-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium">Index</label>
                            <input
                              type="number"
                              defaultValue={group.index}
                              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Version ID</label>
                            <select
                              defaultValue={group.version_id.toString()}
                              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="1">Version 1</option>
                              <option value="2">Version 2</option>
                              <option value="3">Version 3</option>
                            </select>
                          </div>
                        </div>

                        <div className="mt-4">
                          <label className="text-sm font-medium">Group UUID</label>
                          <input
                            defaultValue={group.group_uuid}
                            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs bg-gray-50"
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <button className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                      Hủy thay đổi
                    </button>
                    <button className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      Lưu thay đổi
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Individual Voice Selection Popup */}
      {showVoicePopup && (
        <VoiceSelectionPopup
          isOpen={true}
          onClose={handleCloseVoicePopup}
          languages={languages.filter(lang => lang.id === showVoicePopup.langId)}
          selectedVoices={selectedVoices}
          onVoiceSelect={(languageId, gender, voiceCode) => {
            handleVoiceSelect(languageId, gender, voiceCode);
          }}
          onApplyDefaultVoices={(languageId) => {
            handleApplyDefaultVoices(languageId);
          }}
          singleLanguage={true}
          targetGender={showVoicePopup.gender}
        />
      )}
    </div>
  );
};

export default GroupDetailModal;
